// FILE: SideChatAgentsSection.tsx
// Purpose: Settings → Advanced → Side chat: which managed agents may be picked
//          for a side chat over selected text.
// Layer: Web settings presentation
// Exports: SideChatAgentsSection

import type { ManagedAgent, ManagedAgentId } from "@taskin-code/contracts";
import { useCallback, useEffect, useState } from "react";

import { canRunSideChat } from "@taskin-code/shared/managedAgentMode";

import { AgentsClient } from "~/agents/AgentsClient";

import { Checkbox } from "../ui/checkbox";

/**
 * The same flag the agent editor carries, gathered into one list.
 *
 * Both surfaces write it through the same `PATCH /api/admin/agents/:id`, so there
 * is no second source of truth — what differs is the question each answers. The
 * editor answers "how is this agent configured?", one agent at a time; this
 * answers "who can be picked for a side chat?", which is a decision about the
 * roster and is unreadable when it is spread across a dialog per agent.
 *
 * Team visibility is deliberately *not* restated here. `listVisibleForUser`
 * already narrows the picker by team, and repeating the rule in the admin UI
 * would be a second, weaker copy of it — so the note says so instead.
 *
 * Agent *mode* is restated, though, and for the opposite reason: a subagent-only
 * agent can never be picked for a side chat, so listing it here would offer a
 * checkbox whose save the server refuses. Those rows are dropped and the count
 * is left out of the copy — the place to widen an agent's mode is its own editor.
 */
export function SideChatAgentsSection() {
  const [agents, setAgents] = useState<ReadonlyArray<ManagedAgent> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [savingAgentId, setSavingAgentId] = useState<ManagedAgentId | null>(null);

  useEffect(() => {
    let cancelled = false;
    void AgentsClient.list()
      .then((result) => {
        if (!cancelled) setAgents(result);
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        // Reading the roster needs `a.view`. Someone without it is not an admin
        // for this purpose, so the section says why rather than showing nothing.
        setError(cause instanceof Error ? cause.message : "Could not load the agent roster.");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Filtered on render rather than on load so a mode changed in the agent editor
  // is reflected the next time this section mounts, with no second fetch path to
  // keep in step.
  const eligibleAgents = (agents ?? []).filter((agent) => canRunSideChat(agent.mode));

  const toggleAgent = useCallback(async (agent: ManagedAgent) => {
    setSavingAgentId(agent.id);
    setError(null);
    const nextValue = !agent.sideChatEnabled;
    try {
      const updated = await AgentsClient.update(agent.id, { sideChatEnabled: nextValue });
      setAgents((existing) =>
        (existing ?? []).map((candidate) => (candidate.id === agent.id ? updated : candidate)),
      );
    } catch (cause: unknown) {
      // No optimistic flip to undo: the checkbox reflects the server's answer, so
      // a failed save simply leaves it where it was, with the reason shown.
      setError(cause instanceof Error ? cause.message : "Could not save the change.");
    } finally {
      setSavingAgentId(null);
    }
  }, []);

  if (error && agents === null) {
    return (
      <p className="rounded-xl border border-border/70 px-4 py-3 text-xs text-muted-foreground">
        {error}
      </p>
    );
  }

  if (agents === null) {
    return (
      <p className="rounded-xl border border-border/70 px-4 py-3 text-xs text-muted-foreground">
        Loading agents…
      </p>
    );
  }

  return (
    <div className="space-y-2 rounded-xl border border-border/70 px-4 py-3">
      <p className="text-xs text-muted-foreground">
        Agents offered when someone starts a side chat over selected text. Team visibility still
        applies: a user who cannot see an agent is not offered it. With none enabled, the action
        stays disabled.
      </p>
      {eligibleAgents.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          No agents can run a side chat yet. A subagent-only agent is never offered one.
        </p>
      ) : (
        <div className="flex max-h-64 flex-col gap-1 overflow-y-auto pt-1">
          {eligibleAgents.map((agent) => (
            <label key={agent.id} className="flex items-center gap-2 py-0.5 text-xs">
              <Checkbox
                checked={agent.sideChatEnabled}
                disabled={savingAgentId !== null}
                onCheckedChange={() => void toggleAgent(agent)}
              />
              <span className="text-foreground">{agent.name}</span>
              <span className="text-muted-foreground">
                {agent.provider === "codex" ? "Codex" : "Claude"} · {agent.model}
              </span>
            </label>
          ))}
        </div>
      )}
      {error ? <p className="pt-1 text-xs text-red-400">{error}</p> : null}
    </div>
  );
}
